import React, { useState } from 'react';
import { useAccount } from 'wagmi';
import { useSablierAirdrop } from '../hooks/useSablierAirdrop';
import { SablierCampaign, getCampaignStatus, getTimeRemaining } from '../utils/sablierTypes';
import { PrimaryButton, SecondaryButton } from './Button';
import { Skeleton } from '@mui/material';
import { formatExactDateTime } from '../utils/dateUtils';

interface SablierCampaignRowProps {
  campaign: SablierCampaign;
}

const SablierCampaignRow: React.FC<SablierCampaignRowProps> = ({ campaign }) => {
  const { address, isConnected } = useAccount();
  const { name, cid, link, ends } = campaign;

  // Check eligibility if wallet is connected
  const {
    isEligible,
    isLoading: isCheckingEligibility,
    error,
  } = useSablierAirdrop({
    address: address || '',
    cid,
    enabled: isConnected && !!address,
  });

  // Calculate status and time remaining
  const status = getCampaignStatus(ends);
  const timeRemaining = getTimeRemaining(ends);
  const exactDateTime = formatExactDateTime(ends, false);
  const statusText = status === 'ongoing' ? 'Ongoing' : 'Ended';
  const statusColor = status === 'ongoing' ? 'text-secondary-orange' : 'text-contrast-5';
  const [isHovering, setIsHovering] = useState(false);

  // Determine button state and text
  const getButtonElement = () => {
    // Not connected
    if (!isConnected) {
      return <SecondaryButton disabled>Connect Wallet</SecondaryButton>;
    }

    // Checking eligibility
    if (isCheckingEligibility) {
      return (
        <SecondaryButton disabled>
          <div className="flex items-center gap-2">
            <span>Checking...</span>
            <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
          </div>
        </SecondaryButton>
      );
    }

    // Eligible - show Claim button
    if (isEligible) {
      return <PrimaryButton onClick={() => window.open(link, '_blank')}>Claim</PrimaryButton>;
    }

    // Not eligible (includes 404 from API or any other error state)
    return <SecondaryButton disabled>Not Eligible</SecondaryButton>;
  };

  return (
    <div
      className="bg-white dark:bg-contrast-2 hover:bg-[#FDFDFD] dark:hover:bg-contrast-3 border border-contrast-7 dark:border-transparent transition-colors rounded-lg p-4 md:p-6"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Mobile Layout */}
      <div className="md:hidden flex flex-col gap-3">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-contrast-5 text-xs font-inter mb-1">Campaign</div>
            <div className="text-contrast-3 dark:text-white text-sm font-medium font-inter">
              {name}
            </div>
          </div>
          {getButtonElement()}
        </div>
        <div className="flex justify-between items-center">
          <div>
            <div className="text-contrast-5 text-xs font-inter mb-1">Status</div>
            <div className={`text-sm font-inter ${statusColor}`}>{statusText}</div>
          </div>
          <div>
            <div className="text-contrast-5 text-xs font-inter mb-1">Ends in</div>
            {isCheckingEligibility ? (
              <Skeleton width={60} height={20} />
            ) : (
              <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono cursor-default">
                {isHovering ? exactDateTime : timeRemaining}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:grid gap-6 items-center grid-cols-[2fr_1fr_1.5fr_auto]">
        {/* Campaign Column */}
        <div>
          <div className="text-contrast-5 text-xs font-inter mb-2">Campaign</div>
          <div className="text-contrast-3 dark:text-white text-sm font-medium font-inter">
            {name}
          </div>
        </div>

        {/* Status Column */}
        <div>
          <div className="text-contrast-5 text-xs font-inter mb-2">Status</div>
          <div className={`text-sm font-inter ${statusColor}`}>{statusText}</div>
        </div>

        {/* Ends in Column */}
        <div>
          <div className="text-contrast-5 text-xs font-inter mb-2">Ends in</div>
          {isCheckingEligibility ? (
            <Skeleton width={80} height={20} />
          ) : (
            <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono cursor-default">
              {isHovering ? exactDateTime : timeRemaining}
            </div>
          )}
        </div>

        {/* Action Column */}
        <div className="flex justify-end">{getButtonElement()}</div>
      </div>
    </div>
  );
};

export default SablierCampaignRow;
